/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

// i18n
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useLocation, useNavigate } from 'js/lib/useRouter';

import { Divider, Grid, Hidden, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { DiscountIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

// Components
import { TrackedButton } from 'bundles/enterprise-ui/components';
import ReactPriceDisplay from 'bundles/ownable-products-common/components/ReactPriceDisplay';
import type { Discount } from 'bundles/teams-v2/common/types';
import { QUARTERLY } from 'bundles/teams-v2/constants/billing';

import _t from 'i18n!nls/teams-v2';

export type Props = {
  learnerCount?: number;
  billingType?: number;
  total?: string;
  todaysTotal?: string;
  organizationName?: string;
  unitPrice?: string;
  showEditLink: boolean;
  loading: boolean;
  currency?: string;
} & Partial<Discount>;

function TeamsPurchaseCheckoutSummary(props: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const track = useTracker();

  const styles = {
    container: css`
      border: 1px solid var(--cds-color-grey-300);
      border-radius: 4px;
      overflow: hidden;

      ${breakpoints.down('xs')} {
        border: 0;
        margin: 0 -16px -16px -16px;
        width: calc(100% + 32px);
      }
    `,

    gutterBottom: css`
      margin-bottom: var(--cds-spacing-100);
    `,

    divider: css`
      margin-top: var(--cds-spacing-200);
      margin-bottom: var(--cds-spacing-200);
    `,

    discount: css`
      color: var(--cds-color-green-600);
    `,

    highlighted: css`
      background-color: var(--cds-color-blue-50);
      padding: var(--cds-spacing-300);
    `,
  };

  const query = location?.query;

  const {
    learnerCount,
    billingType,
    total,
    todaysTotal,
    organizationName,
    unitPrice,
    showEditLink,
    currency,
    loading,
    discountPercentage,
    discountAmount,
    originalPrice,
  } = props;

  const onClick = () => {
    track('click_button', {
      button: {
        name: 'wes_funnel_edit_plan',
      },
    });
    navigate({
      pathname: '/purchase/plan',
      search: '?' + new URLSearchParams(query as Record<string, string>).toString(),
    });
  };

  const dashSpan = <span>-</span>;

  const originalTotal = originalPrice ? (
    <ReactPriceDisplay value={parseFloat(originalPrice)} currency={currency} hideCurrencyCode />
  ) : (
    dashSpan
  );
  const unitPriceEl = <ReactPriceDisplay value={parseFloat(unitPrice ?? '')} currency={currency} hideCurrencyCode />;
  const totalEl =
    !loading && total ? <ReactPriceDisplay value={parseFloat(total)} currency={currency} hideCurrencyCode /> : dashSpan;
  const installmentAmountEl =
    !loading && todaysTotal ? (
      <ReactPriceDisplay value={parseFloat(todaysTotal)} currency={currency} hideCurrencyCode />
    ) : (
      dashSpan
    );

  const todaysTotalDescEl = _t('14-day refund guarantee');

  let breakdownTitle;
  let breakdownItems: [string, string | JSX.Element][];
  switch (billingType) {
    case QUARTERLY:
      breakdownTitle = _t('Quarterly Payments');
      breakdownItems = [
        [_t('Annual total'), <span className="font-weight-bold">{totalEl}</span>],
        [_t('4x quarterly payments'), installmentAmountEl],
      ];
      break;
    default:
      breakdownTitle = _t('Annual Payment');
      breakdownItems = [[_t('Annual total'), <span className="font-weight-bold">{installmentAmountEl}</span>]];
      break;
  }

  let discountAmountEl: string | React.ReactElement = dashSpan;
  if (discountAmount) {
    discountAmountEl = (
      <span>
        -<ReactPriceDisplay currency={currency} value={Math.abs(parseFloat(discountAmount))} hideCurrencyCode />
      </span>
    );
  }

  return (
    <div className="rc-TeamsPurchaseCheckoutSummary" css={styles.container}>
      <div css={{ padding: `var(--cds-spacing-400) var(--cds-spacing-300) var(--cds-spacing-200)` }}>
        <Hidden xsDown>
          <Typography variant="h2" css={styles.gutterBottom}>
            {_t('Purchase Summary')}
          </Typography>

          <Grid container justifyContent="space-between" alignItems="flex-start">
            <Grid item>
              <Typography2 variant="subtitleMedium" component="div">
                {_t('Selected subscription')}
              </Typography2>
              {organizationName && (
                <Typography2 variant="subtitleMedium" component="div">
                  <FormattedMessage message={_t('for {organizationName}')} organizationName={organizationName} />
                </Typography2>
              )}
            </Grid>
            <Grid item>
              {showEditLink && (
                <TrackedButton
                  trackingName="edit_plan_button"
                  data-testid="edit-plan-button"
                  className="c-editLink p-a-0"
                  variant="ghost"
                  onClick={onClick}
                >
                  {_t('edit')}
                </TrackedButton>
              )}
            </Grid>
          </Grid>
        </Hidden>

        <Grid container justifyContent="space-between" wrap="nowrap" spacing={24}>
          <Grid item>
            <Typography2 component="p">{_t('Team plan (price per user per year)')}</Typography2>
          </Grid>
          <Grid item>
            <Typography2 component="p">{unitPriceEl}</Typography2>
          </Grid>
        </Grid>

        <Grid container justifyContent="space-between" wrap="nowrap" spacing={24}>
          <Grid item>
            <Typography2 component="p">{_t('Number of users')}</Typography2>
          </Grid>
          <Grid item>
            <Typography2 component="p">{learnerCount ? `x ${learnerCount}` : dashSpan}</Typography2>
          </Grid>
        </Grid>

        <Grid container justifyContent="space-between" wrap="nowrap" spacing={24}>
          <Grid item>
            <Typography2 component="p">{_t('Total')}</Typography2>
          </Grid>
          <Grid item>
            <Typography2 component="p">{originalTotal}</Typography2>
          </Grid>
        </Grid>

        {Boolean(discountPercentage) && (
          <>
            <Divider css={styles.divider} />
            <Typography2 component="h3" variant="subtitleMedium">
              Promotion
            </Typography2>
            <Grid container justifyContent="space-between" direction="row" wrap="nowrap">
              <Grid item container direction="row" alignItems="center">
                <DiscountIcon css={[styles.discount, { marginRight: 'var(--cds-spacing-100)' }]} />
                <Typography2 component="p" css={styles.discount}>
                  {discountPercentage}% discount
                </Typography2>
              </Grid>
              <Grid item>
                <Typography2 component="p" css={styles.discount}>
                  {discountAmountEl}
                </Typography2>
              </Grid>
            </Grid>
          </>
        )}

        <Divider css={styles.divider} />
        <Typography2 component="h3" variant="subtitleMedium">
          {breakdownTitle}
        </Typography2>
        {breakdownItems.map((pair) => (
          <Grid container justifyContent="space-between" wrap="nowrap" spacing={24}>
            <Grid item>
              <Typography2 component="p">{pair[0]}</Typography2>
            </Grid>
            <Grid item>
              <Typography2 component="p">{pair[1]}</Typography2>
            </Grid>
          </Grid>
        ))}
      </div>
      <div css={styles.highlighted}>
        <Grid container justifyContent="space-between" wrap="nowrap" spacing={24}>
          <Grid item>
            <Typography2 component="h3" variant="subtitleMedium">
              {_t("Today's Payment")}
            </Typography2>
          </Grid>
          <Grid item>
            <Typography2 variant="subtitleMedium" component="div">
              {installmentAmountEl}
            </Typography2>
          </Grid>
        </Grid>
        <Typography2 component="p">{todaysTotalDescEl}</Typography2>
      </div>
    </div>
  );
}

export default TeamsPurchaseCheckoutSummary;
